import React, { useState } from 'react';
import Tooltip from 'components/Tooltip/Tooltip';
import { FormikProps } from 'formik';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import { formatNumber } from 'utils/currency.helpers';
import formatIsoDate from 'utils/formatIsoDate';

import { TradeDto } from '@alpha/fx-dtos';
import { DynamicTable } from '@alpha/ui-lib/ui/DynamicTable/DynamicTable';
import { IconButton } from '@alpha/ui-lib/ui/external';
import {
  faTrashAlt,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { MassDrawdownRequestForm } from '../formData';

import useStyles from './index.styles';

interface MassDrawdownTableProps {
  selectedTrades: TradeDto[];
  handleAmountChange: (amount: number, trade: TradeDto) => void;
  requestForm: FormikProps<MassDrawdownRequestForm>;
  handleAddAdditionalTrade: (tradeId: string) => void;
  setTradeIdToRemove: (tradeId: string) => void;
  setConfirmationModalType: (type: 'CLEAR' | 'CLOSE' | 'REMOVE') => void;
  setOpenConfirmation: (open: boolean) => void;
  additionalTrades: TradeDto[];
  tradeErrors: Record<string, string>;
}

const MassDrawdownTable: React.FC<MassDrawdownTableProps> = ({
  selectedTrades,
  handleAmountChange,
  requestForm,
  handleAddAdditionalTrade,
  setTradeIdToRemove,
  setConfirmationModalType,
  setOpenConfirmation,
  additionalTrades,
  tradeErrors,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [openAdditionalRowDropdown, setOpenAdditionalRowDropdown] = useState(false);

  const columns = [
    {
      header: 'Trade ID',
      accessor: 'contractNumber',
      width: 80,
      columnStyle: classes.link,
    },
    {
      header: 'Currency Pair',
      accessor: 'currencyPair',
      width: 120,
    },
    {
      header: 'Buy Balance',
      accessor: 'buyBalance',
      width: 120,
      columnStyle: classes.bold,
    },
    {
      header: 'Sell Balance',
      accessor: 'sellBalance',
      width: 120,
      columnStyle: classes.bold,
    },
    {
      header: 'Rate',
      accessor: 'rate',
      width: 20,
      columnStyle: classes.link,
    },
    {
      header: 'Drawdown Amount',
      accessor: 'drawdownAmount',
      width: 200,
    },
    {
      header: '',
      accessor: 'action',
      width: 40,
    },
  ];

  const getTooltip = (splitType: string): string => {
    if (splitType === 'split-evenly') return t('cannot_edit_due_to_even_split');
    if (splitType === 'placeholder' || requestForm.values.fixCurrencyCode === 'placeholder') return t('please_select_a_split_method');
    return '';
  };

  return (
    <DynamicTable
      data={selectedTrades.map((trade) => ({
        contractNumber: {
          value: trade.contractNumber ?? '-',
        },
        currencyPair: {
          value: (
            <div>
              {trade.currencyPair?.slice(0, 7)}
              <span style={{ fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                {` ${trade.currencyPair?.slice(7)}`}
              </span>
            </div>),
        },
        buyBalance: {
          value: `${trade.buyBalance?.toFixed(2)} ${trade.buyCurrencyCode}`,
        },
        sellBalance: {
          value: `${trade.sellBalance?.toFixed(2)} ${trade.soldCurrencyCode}`,
        },
        rate: {
          value: trade.rate?.toFixed(4),
        },
        drawdownAmount: {
          value: '',
          borderLeft: '1px solid #EAEAEA',
          numericalInput: true,
          inputProps: {
            inputPlaceholder: t('click_to_enter_amount'),
            disabled: requestForm.values?.splitType !== 'manually-enter' || requestForm.values?.fixCurrencyCode === 'placeholder',
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => debounce(() => {
              const amount = parseFloat(e.target.value.replace(/,/g, ''));
              handleAmountChange(amount, trade);
            }, 100)(),
            value:
              String(formatNumber(requestForm.values?.drawdowns.find((tr) => tr.tradeId === trade.id)?.amount as number, 2, false, true)) || '',
            tooltip: getTooltip(requestForm.values?.splitType),
          },
          error: tradeErrors[trade.id],
        },
        action: {
          value: (
            <Tooltip
              title={selectedTrades.length < 3 ? t('minimum_two_contracts_required') : ''}
              classes={{ tooltip: classes.tooltip }}
              arrow={false}
            >
              <div>
                <IconButton
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    setTradeIdToRemove(trade.id);
                    setConfirmationModalType('REMOVE');
                    setOpenConfirmation(true);
                  }}
                  style={{ padding: '0px', opacity: selectedTrades.length < 3 ? 0.25 : 1 }}
                  disabled={selectedTrades.length < 3}
                >
                  <FontAwesomeIcon
                    icon={faTrashAlt}
                    color="#A62B23"
                    size="xs"
                  />
                </IconButton>
              </div>
            </Tooltip>
          ),
          borderLeft: '1px solid #EAEAEA',
        },
      }))}
      columns={columns}
      testId="mass-drawdown-details-table"
      // scrollable
      // scrollableMaxHeight={300}
      addAdditionalRow
      additionalRowProps={{
        text: t('add_additional_forward_contracts'),
        dropdownText: t('choose_a_forward_contract'),
        columns,
        data: additionalTrades.length ? additionalTrades.map((trade) => ({
          contractNumber: {
            value: trade.contractNumber || '-',
          },
          currencyPair: {
            value: (
              <div>
                {trade.currencyPair?.slice(0, 7)}
                <span style={{ fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                  {` ${trade.currencyPair?.slice(7)}`}
                </span>
              </div>),
          },
          buyBalance: {
            value: `${trade.buyBalance?.toFixed(2)} ${trade.buyCurrencyCode}`,
          },
          sellBalance: {
            value: `${trade.sellBalance?.toFixed(2)} ${trade.soldCurrencyCode}`,
          },
          rate: {
            value: trade.rate?.toFixed(4),
          },
          drawdownAmount: {
            value: '',
            colSpan: 0,
          },
          action: {
            value: (
              <>
                <span style={{ color: '#00000040', marginRight: '8px' }}>
                  Value Date:
                </span>
                {formatIsoDate(trade.valueDate)}
              </>
            ),
            colSpan: 2,
            textAlign: 'right',
            onClick: (e: React.MouseEvent<HTMLTableRowElement>) => {
              e.stopPropagation();
              handleAddAdditionalTrade(trade.id);
              setOpenAdditionalRowDropdown(false);
            },
          },
        })) : [],
        openAdditionalRowDropdown,
        setOpenAdditionalRowDropdown,
      }}
    />
  );
};

export default MassDrawdownTable;
